import React, { useState, useEffect, useRef } from 'react';
import { Tab } from '@headlessui/react';
import { PencilIcon, TrashIcon, ArrowsRightLeftIcon } from '@heroicons/react/20/solid';
import Button from '../common/Button';
import Select from '../common/Select';
import { FunnelIcon } from '@heroicons/react/24/solid';
import ConfirmationDialog from '../common/ConfirmationDialog';
import apiService from '../../services/ApiService';
import { apiRoutes } from '../../shared/apiRoutes';
import { itemsPerPageOptions } from '../../shared/constants';
import CreateGameDialog from './CreateGame';
import { Game, User } from '../../shared/interfaces';
import GameFilterDropdown from './GameFilter';
import { getGameTitle, cleanObject, formatDateOnly } from '../../utils/util';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthorization } from '../../auth/useAuthorization';
import { GameType } from '../../shared/enume';

// Helper function to extract user name from User object or string
const getUserName = (user: User | string | undefined): string => {
  if (!user) return 'N/A';
  if (typeof user === 'string') return user;
  return user.name || user.username || 'Unknown';
};

interface GameListRequest {
  id: number | null;
  gameType: string;
  teamA: string;
  teamB: string;
  modifiedBy: string;
  modifiedOn: string | Date;
  page: number;
  pageSize: number;
  orderBy: string;
  orderDirection: string;
}

interface GameListResponse {
  id: number;
  teamA: string;
  teamB: string;
  modifiedBy: string;
  modifiedOn: string | Date;
}

const ITEMS_PER_PAGE = 10;
interface GameListPaginationProps {
  gameType?: string; // Optional prop to specify game type  
}

const GameListPagination: React.FC<GameListPaginationProps> = ({gameType: initialGameType}) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Determine initial game type from prop or URL path
  const getInitialGameType = (): string => {
    if (initialGameType) return initialGameType.toLowerCase();
    if (location.pathname.includes('/test')) return GameType.test;
    if (location.pathname.includes('/session')) return GameType.session;
    return GameType.oneday; // Default to oneday
  };

  const initialGameTypeValue = getInitialGameType();
  const [selectedGameType, setSelectedGameType] = useState<string>(initialGameTypeValue);
  const [showFilter, setShowFilter] = useState(false);
  const [gameTitle, setGameTitle] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(ITEMS_PER_PAGE);
  const [createGameOpen, setCreateGameOpen] = useState(false);
  const [editGame, setEditGame] = useState<Game | null>(null);
  const [games, setGames] = useState<Game[]>([]);
  const [paginatedGame, setPaginatedGame] = useState<Game[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [orderBy, setOrderBy] = useState<keyof Game>('createdOn');
  const [orderDirection, setOrderDirection] = useState<'asc' | 'desc'>('desc');
  const [gameId, setGameId] = useState(0);
  const [gameListRequest, setGameListRequest] = useState<GameListRequest>({
    id: null,
    gameType: initialGameTypeValue,
    teamA: '',
    teamB: '',  
    modifiedBy: '',
    modifiedOn: '',
    page: 1,
    pageSize: ITEMS_PER_PAGE,
    orderBy: 'createdOn',
    orderDirection: 'desc',
  });
  
  // Track previous gameType to detect changes
  const prevGameTypeRef = useRef<string>(initialGameTypeValue);
  const [open, setOpen] = useState(false);
  const [deleteId, setdeleteId] = useState(0);
  const { hasRole } = useAuthorization();
  const isPartner = hasRole('Partner');

  // Update selected game type when prop or URL changes
  useEffect(() => {
    const newGameType = getInitialGameType();
    if (prevGameTypeRef.current.toLowerCase() !== newGameType.toLowerCase()) {
      prevGameTypeRef.current = newGameType;
      setLoading(true); // Show loading when game type changes from URL/prop
      setSelectedGameType(newGameType);
      setCurrentPage(0);
      // Create a new request object to ensure React detects the change
      const updatedRequest: GameListRequest = {
        id: null,
        gameType: newGameType,
        teamA: '',
        teamB: '',
        modifiedBy: '',
        modifiedOn: '',
        page: 1,
        pageSize: gameListRequest.pageSize,
        orderBy: gameListRequest.orderBy,
        orderDirection: gameListRequest.orderDirection,
      };
      setGameListRequest(updatedRequest);
    }
  }, [initialGameType, location.pathname]);

  // Update game title when selected game type changes
  useEffect(() => {
    setGameTitle(getGameTitle(selectedGameType));
  }, [selectedGameType]);

  // Handle radio button change
  const handleGameTypeChange = (newGameType: string) => {
    const normalizedType = newGameType.toLowerCase();
    if (selectedGameType.toLowerCase() === normalizedType) return; // Already selected
    
    setLoading(true); // Show loading when changing game type
    setSelectedGameType(normalizedType);
    setCurrentPage(0);
    
    // Update game list request - create new object to ensure React detects change
    const updatedRequest: GameListRequest = {
      id: null,
      gameType: normalizedType,
      teamA: gameListRequest.teamA,
      teamB: gameListRequest.teamB,
      modifiedBy: gameListRequest.modifiedBy,
      modifiedOn: gameListRequest.modifiedOn,
      page: 1,
      pageSize: gameListRequest.pageSize,
      orderBy: gameListRequest.orderBy,
      orderDirection: gameListRequest.orderDirection,
    };
    setGameListRequest(updatedRequest);
    
    // Navigate to the appropriate route
    const routeMap: Record<string, string> = {
      [GameType.oneday]: '/game/oneday',
      [GameType.test]: '/game/test',
      [GameType.session]: '/game/session',
    };
    const route = routeMap[normalizedType] || '/game/oneday';
    navigate(route);
  };

  useEffect(() => {
    if(createGameOpen) {return; }
    setLoading(true); // Show loading when fetching games
    apiService.post<Game[]>(apiRoutes.games.list, {      
        ...cleanObject(gameListRequest),
        page: currentPage + 1,
        isGameOver: false,
    })
      .then(data => {
        setGames(data.data);
        setTotalPages(Math.ceil(data.totalCount / gameListRequest.pageSize));
        setPaginatedGame(data.data);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Failed to fetch games:', error);
        setLoading(false);
      });
  }, [gameListRequest, currentPage, createGameOpen]);

  const handleDelete = async (id: number) => {
    setOpen(true);
    setTimeout(() => {
      setdeleteId(id);
    },0);
  }

  const callDeleteApi = async () => {
    // Show loading state while deleting  
    setLoading(true);
    try {
      apiService.delete(`/Game/${deleteId}`)
        .then(() => {
          setGames(prev => prev.filter(g => g.id !== deleteId));
          setPaginatedGame(prev => prev.filter(g => g.id !== deleteId));
          window.dispatchEvent(new CustomEvent('show-toast', { detail: { message: 'Game deleted successfully.', type: 'success' } }));
        })
        .catch((err: any) => {
          const message = err?.message || err?.error || 'Failed to delete game.';
          window.dispatchEvent(new CustomEvent('show-toast', { detail: { message, type: 'error' } }));
        }); 
    } catch {
      window.dispatchEvent(new CustomEvent('show-toast', { detail: { message: 'An error occurred while deleting the game.', type: 'error' } }));
    } finally {
      setOpen(false)
      setdeleteId(0)
      setLoading(false);
    }
  };

  // Add this function to handle filter application
  const applyFiltersToGameList = (filters: Partial<GameListRequest>) => {
    setGameListRequest(prev => ({
      ...prev,
      ...filters,
      page: 1, // Reset to first page on filter
    }));
  };


  const handleShowHideFunner = () => {
    setShowFilter(prev => !prev);
  }

  const handleSort = (column: keyof Game) => {
    const newOrderDirection = orderBy === column && orderDirection === 'asc' ? 'desc' : 'asc';
    setOrderBy(column);
    setOrderDirection(newOrderDirection);
    setCurrentPage(0); // Reset to first page when sorting changes
    setGameListRequest(prev => ({
      ...prev,
      orderBy: column,
      orderDirection: newOrderDirection,
      page: 1, // Reset to first page when sorting changes
    }));
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage((rowsPerPage) => newRowsPerPage);
    setTotalPages((totalPages) => Math.ceil(games.length / newRowsPerPage));
    setPaginatedGame(games.slice(
      0,
      newRowsPerPage
    ));
    setCurrentPage(0);
    setGameListRequest(prev => ({
      ...prev,
      pageSize: newRowsPerPage,
      page: 1,
    }));
  };

  const handleTransactions = (id: number) => {
    // Navigate to transactions page or open transactions dialog
    setGameId(id);
    navigate(`/game/transactions/${id}`);
  };

  const handleEdit = (game: Game) => {
    setEditGame(game);
    setCreateGameOpen(true);
  };

  const handleCreateGameClick = () => {
    setEditGame(null);
    setCreateGameOpen(true);
  };

  // Listen for CTRL+G keyboard shortcut and open-create-game event
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle CTRL+G when not typing in an input
      if (document.activeElement?.tagName === 'INPUT' || document.activeElement?.tagName === 'TEXTAREA') {
        return;
      }

      if (e.ctrlKey && (e.key === 'g' || e.key === 'G')) {
        e.preventDefault();
        setEditGame(null);
        setCreateGameOpen(true);
      }
    };

    const handleOpenCreateGame = () => {
      setEditGame(null);
      setCreateGameOpen(true);
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('open-create-game', handleOpenCreateGame);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('open-create-game', handleOpenCreateGame);
    };
  }, []);

  const handlePageClick = (page: number) => {
    setCurrentPage(page - 1);
    setGameListRequest(prev => ({
      ...prev,
      page: page,
    }));
  };

  const goToPrevious = () => {
    setCurrentPage(prev => Math.max(prev - 1, 0));
    setGameListRequest(prev => ({
      ...prev,
      page: Math.max(prev.page - 1, 1),
    }));
  };

  const goToNext = () => {
    setCurrentPage(prev => Math.min(prev + 1, totalPages - 1));
    setGameListRequest(prev => ({
      ...prev,
      page: Math.min(prev.page + 1, totalPages),
    }));
  };

  return (
    <div className="p-4 w-full mx-auto bg-gradient-to-br from-slate-50 to-slate-100 min-h-screen">
      <CreateGameDialog isOpen={createGameOpen} onClose={(closed) => { setCreateGameOpen(closed); if (!closed) setEditGame(null); }} gameType={selectedGameType} editGame={editGame} />
      
      {/* Header Section */}
      <div className="bg-white rounded-lg shadow-md mb-4 border border-slate-200">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center p-4 gap-4">
          <div className="flex items-center gap-3">
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-2 rounded-lg shadow-sm">
              <ArrowsRightLeftIcon className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-800">{gameTitle} Game</h2>
              <p className="text-sm text-slate-500 mt-0.5">Manage and view your games</p>
            </div>
          </div>
          
          <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
            {/* Game Type Radio Buttons */}
            <div className="inline-flex items-stretch gap-1 bg-slate-50 p-1 rounded-lg border border-slate-200">
              <label className={`flex min-w-[5.5rem] items-center justify-center cursor-pointer px-3 py-1.5 rounded-md transition-all ${
                selectedGameType.toLowerCase() === GameType.oneday 
                  ? 'bg-blue-500 text-white shadow-sm' 
                  : 'text-slate-700 hover:bg-slate-100'
              }`}>
                <input
                  type="radio"
                  name="gameType"
                  value={GameType.oneday}
                  checked={selectedGameType.toLowerCase() === GameType.oneday}
                  onChange={(e) => handleGameTypeChange(e.target.value)}
                  className="sr-only"
                />
                <span className="text-sm font-medium leading-none whitespace-nowrap">One Day</span>
              </label>
              <label className={`flex min-w-[5.5rem] items-center justify-center cursor-pointer px-3 py-1.5 rounded-md transition-all ${
                selectedGameType.toLowerCase() === GameType.test 
                  ? 'bg-blue-500 text-white shadow-sm' 
                  : 'text-slate-700 hover:bg-slate-100'
              }`}>
                <input
                  type="radio"
                  name="gameType"
                  value={GameType.test}
                  checked={selectedGameType.toLowerCase() === GameType.test}
                  onChange={(e) => handleGameTypeChange(e.target.value)}
                  className="sr-only"
                />
                <span className="text-sm font-medium leading-none whitespace-nowrap">Test</span>
              </label>
              <label className={`flex min-w-[5.5rem] items-center justify-center cursor-pointer px-3 py-1.5 rounded-md transition-all ${
                selectedGameType.toLowerCase() === GameType.session 
                  ? 'bg-blue-500 text-white shadow-sm' 
                  : 'text-slate-700 hover:bg-slate-100'
              }`}>
                <input
                  type="radio"
                  name="gameType"
                  value={GameType.session}
                  checked={selectedGameType.toLowerCase() === GameType.session}
                  onChange={(e) => handleGameTypeChange(e.target.value)}
                  className="sr-only"
                />
                <span className="text-sm font-medium leading-none whitespace-nowrap">Session</span>
              </label>
            </div>
            
            <div className="flex items-center gap-2 text-slate-700">
              <span className="text-sm font-medium">Rows:</span>
              <Select options={itemsPerPageOptions} onChange={handleRowsPerPageChange} />
            </div>
            
            {/* RBAC: Hide Create Game button for Partners */}
            {!isPartner && (
              <Button 
                className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-medium px-4 py-2 rounded-lg shadow-sm transition-all duration-200" 
                onClick={handleCreateGameClick}
              >
                Create Game
              </Button>
            )}
            
            <button
              onClick={handleShowHideFunner}
              className={`p-2 rounded-lg transition-all duration-200 ${
                showFilter 
                  ? 'bg-blue-500 text-white shadow-sm' 
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              <FunnelIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
      
      {showFilter && (
        <div className="mb-4">
          <GameFilterDropdown applyFiltersToGameList={applyFiltersToGameList} gameType={selectedGameType} />
        </div>
      )}

      {/* Table Section */}
      <div className="bg-white rounded-lg shadow-md border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-gradient-to-r from-slate-50 to-slate-100">
              <tr>
                <th 
                  className="px-4 py-2 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider cursor-pointer hover:bg-slate-100 transition-colors"
                  onClick={() => handleSort('teamA')}
                >
                  <div className="flex items-center gap-2">
                    <span>Nation A</span>
                    {orderBy === 'teamA' && (
                      <span className="text-blue-500">{orderDirection === 'asc' ? '▲' : '▼'}</span>
                    )}
                  </div>
                </th>
                {selectedGameType.toLowerCase() === GameType.session ? (
                  <th 
                    className="px-4 py-2 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider cursor-pointer hover:bg-slate-100 transition-colors"
                    onClick={() => handleSort('overs')}
                  >
                    <div className="flex items-center gap-2">
                      <span>Overs</span>
                      {orderBy === 'overs' && (
                        <span className="text-blue-500">{orderDirection === 'asc' ? '▲' : '▼'}</span>
                      )}
                    </div>
                  </th>
                ) : (
                  <th 
                    className="px-4 py-2 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider cursor-pointer hover:bg-slate-100 transition-colors"
                    onClick={() => handleSort('teamB')}
                  >
                    <div className="flex items-center gap-2">
                      <span>Nation B</span>
                      {orderBy === 'teamB' && (
                        <span className="text-blue-500">{orderDirection === 'asc' ? '▲' : '▼'}</span>
                      )}
                    </div>
                  </th>
                )}
                <th className="px-4 py-2 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Modified By</th>
                <th 
                  className="px-4 py-2 text-center text-xs font-semibold text-slate-700 uppercase tracking-wider cursor-pointer hover:bg-slate-100 transition-colors"
                  onClick={() => handleSort('createdOn')}
                >
                  <div className="flex items-center justify-center gap-2">
                    <span>Created On</span>
                    {orderBy === 'createdOn' && (
                      <span className="text-blue-500">{orderDirection === 'asc' ? '▲' : '▼'}</span>
                    )}
                  </div>
                </th>
                <th 
                  className="px-4 py-2 text-center text-xs font-semibold text-slate-700 uppercase tracking-wider cursor-pointer hover:bg-slate-100 transition-colors"
                  onClick={() => handleSort('modifiedOn')}
                >
                  <div className="flex items-center justify-center gap-2">
                    <span>Modified On</span>
                    {orderBy === 'modifiedOn' && (
                      <span className="text-blue-500">{orderDirection === 'asc' ? '▲' : '▼'}</span>
                    )}
                  </div>
                </th>
                <th className="px-4 py-2 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-200">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mb-3"></div>
                      <p className="text-slate-500 text-sm font-medium">Loading games...</p>
                    </div>
                  </td>
                </tr>
              ) : paginatedGame.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <div className="bg-slate-100 rounded-full p-3 mb-3">
                        <ArrowsRightLeftIcon className="h-6 w-6 text-slate-400" />
                      </div>
                      <p className="text-slate-600 text-sm font-medium">No games found</p>
                      <p className="text-slate-400 text-xs mt-1">Try adjusting your filters or create a new game</p>
                    </div>
                  </td>
                </tr>
              ) : (
                paginatedGame.map((game, index) => (
                  <tr 
                    key={game.id} 
                    className={`hover:bg-blue-50 transition-colors duration-150 ${
                      index % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'
                    }`}
                  >
                    <td className="px-4 py-2 whitespace-nowrap">
                      <div className="text-sm font-semibold text-slate-900">{game.teamA}</div>
                    </td>
                    {selectedGameType.toLowerCase() === GameType.session ? (
                      <td className="px-4 py-2 whitespace-nowrap">
                        <div className="text-sm text-slate-700">{game.overs ?? '-'}</div>
                      </td>
                    ) : (
                      <td className="px-4 py-2 whitespace-nowrap">
                        <div className="text-sm font-semibold text-slate-900">{game.teamB}</div>
                      </td>
                    )}
                    <td className="px-4 py-2 whitespace-nowrap">
                      <div className="text-sm text-slate-700">{getUserName(game.modifiedBy)}</div>
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-center">
                      <div className="text-sm text-slate-600">{formatDateOnly(game.createdOn || game.createdAt) || '—'}</div>
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-center">
                      <div className="text-sm text-slate-600">{formatDateOnly(game.modifiedAt || game.modifiedOn) || '—'}</div>
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleTransactions(game.id)}
                          className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 p-1 rounded-md transition-all duration-200"
                          title="View Transactions"
                        >
                          <ArrowsRightLeftIcon className="h-4 w-4" />
                        </button>
                        {/* RBAC: Hide edit and delete buttons for Partners */}
                        {!isPartner && (
                          <>
                            {!game.hasSodas && (
                            <button
                              onClick={() => handleEdit(game)}
                              className="text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 p-1 rounded-md transition-all duration-200"
                              title="Edit Game"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                            )}
                            <button
                              onClick={() => handleDelete(game.id)}
                              className="text-red-600 hover:text-red-800 hover:bg-red-50 p-1 rounded-md transition-all duration-200"
                              title="Delete Game"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 0 && (
          <div className="bg-slate-50 px-6 py-4 border-t border-slate-200">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              <div className="text-sm text-slate-600">
                Showing page <span className="font-semibold text-slate-900">{currentPage + 1}</span> of{' '}
                <span className="font-semibold text-slate-900">{totalPages}</span>
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={goToPrevious}
                  disabled={currentPage === 0}
                  className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                    currentPage === 0
                      ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                      : 'bg-white text-slate-700 hover:bg-blue-50 hover:text-blue-600 border border-slate-300 shadow-sm'
                  }`}
                >
                  Previous
                </button>
                
                <div className="flex items-center gap-1">
                  {Array.from({ length: totalPages }, (_, index) => {
                    const pageNumber = index + 1;
                    const isVisible = 
                      pageNumber === 1 || 
                      pageNumber === totalPages || 
                      (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 3);
                    
                    return isVisible ? (
                      <button
                        key={index}
                        onClick={() => handlePageClick(pageNumber)}
                        className={`px-3 py-2 rounded-lg font-medium transition-all duration-200 min-w-[40px] ${
                          currentPage === index
                            ? 'bg-blue-500 text-white shadow-md'
                            : 'bg-white text-slate-700 hover:bg-blue-50 hover:text-blue-600 border border-slate-300'
                        }`}
                      >
                        {pageNumber}
                      </button>
                    ) : pageNumber === currentPage - 2 || pageNumber === currentPage + 4 ? (
                      <span key={index} className="px-2 py-2 text-slate-400">...</span>
                    ) : null;
                  })}
                </div>
                
                <button
                  onClick={goToNext}
                  disabled={currentPage === totalPages - 1}
                  className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                    currentPage === totalPages - 1
                      ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                      : 'bg-white text-slate-700 hover:bg-blue-50 hover:text-blue-600 border border-slate-300 shadow-sm'
                  }`}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <ConfirmationDialog
        isOpen={open}
        title="Delete Item"
        message="Are you sure you want to delete this item? This action cannot be undone."
        onConfirm={callDeleteApi}
        onCancel={() => setOpen(false)}
      />
    </div>
  );
};

export default GameListPagination;
